import axios from 'axios';
import { toast } from 'react-toastify';
import {
  CognitoIdentityClient,
  GetIdCommand,
  GetCredentialsForIdentityCommand,
  Credentials,
} from '@aws-sdk/client-cognito-identity';
import mqtt, { MqttClient } from 'mqtt';
import CryptoJS from 'crypto-js';
import {
  updateDeviceStatus,
  updateSelectedDeviceStatus,
} from '../store/devices/actionCreators';
import { populateCustomers } from '../store/customers/actionCreators';
import { updateFacilityDeviceStatus } from '../store/facility/actionCreators';
import { Owner } from '../store/customers/customerTypes';
import { DashboardUser } from '../store/dashboardUser/dashboardUserTypes';
import { mqttConnected, mqttDisconnected } from '../store/mqtt/actionCreators';
import {
  setIncomingCall,
  setCallStatus,
  parseIncomingCall,
  parseCallStatus,
} from '../store/calls/actionCreators';
import { CallsActionTypes } from '../store/calls/callsTypes';
import {
  MQTTStoreActionTypes,
  MQTTStoreAction,
  MQTTStore,
  MQTTConnectionEvent,
} from '../store/mqtt/mqttTypes';
import {
  getJSONOrString,
  formatUrl,
  getCookie,
  deleteToken,
} from '../shared/utils';
import { restHost } from '../apiConfig';

const {
  REACT_APP_AWS_COGNITO_IDENTITY_POOL_ID,
  REACT_APP_AWS_COGNITO_USER_POOL_ID,
  REACT_APP_MQTT_CLIENT_PREFIX,
} = import.meta.env;

let publishMessageQueue: Array<any> = [];

// Create WebSocket URL with AWS Signature V4 (based on Flutter implementation)
function createWebSocketUrl(
  endpoint: string,
  region: string,
  credentials: Credentials,
): string {
  const now = new Date();
  const dateTime = now.toISOString().replace(/[:\-]|\.\d{3}/g, '');
  const dateStamp = dateTime.slice(0, 8);
  const service = 'iotdevicegateway';
  const algorithm = 'AWS4-HMAC-SHA256';
  const aws4Request = 'aws4_request';

  // Build credential scope
  const credentialScope = `${dateStamp}/${region}/${service}/${aws4Request}`;

  // Build query parameters (without signature and session token yet)
  const queryParams: Record<string, string> = {
    'X-Amz-Algorithm': algorithm,
    'X-Amz-Credential': `${credentials.AccessKeyId}/${credentialScope}`,
    'X-Amz-Date': dateTime,
    'X-Amz-SignedHeaders': 'host',
    'X-Amz-Expires': '86400',
  };

  // Build canonical query string
  const canonicalQueryString = buildCanonicalQueryString(queryParams);

  // Build canonical request
  const canonicalRequest = buildCanonicalRequest(
    'GET',
    '/mqtt',
    queryParams,
    { host: endpoint },
    '', // empty payload
  );

  // Hash the canonical request
  const hashedCanonicalRequest = CryptoJS.SHA256(canonicalRequest).toString(
    CryptoJS.enc.Hex,
  );

  // Build string to sign
  const stringToSign = buildStringToSign(
    dateTime,
    credentialScope,
    hashedCanonicalRequest,
  );

  // Calculate signing key
  const signingKey = calculateSigningKey(
    credentials.SecretKey!,
    dateTime,
    region,
    service,
  );

  // Calculate signature
  const signature = CryptoJS.HmacSHA256(stringToSign, signingKey).toString(
    CryptoJS.enc.Hex,
  );

  // Build final URL with signature and session token
  const sessionToken = credentials.SessionToken
    ? `&X-Amz-Security-Token=${encodeURIComponent(credentials.SessionToken)}`
    : '';
  const finalUrl = `wss://${endpoint}/mqtt?${canonicalQueryString}&X-Amz-Signature=${signature}${sessionToken}`;

  return finalUrl;
}

function buildCanonicalQueryString(
  queryParams: Record<string, string>,
): string {
  // Sort parameters by key and encode properly
  const sortedKeys = Object.keys(queryParams).sort();
  return sortedKeys
    .map(
      (key) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(queryParams[key])}`,
    )
    .join('&');
}

function buildCanonicalRequest(
  method: string,
  path: string,
  queryParams: Record<string, string>,
  headers: Record<string, string>,
  payload: string,
): string {
  const canonicalQueryString = buildCanonicalQueryString(queryParams);

  // Build canonical headers
  const sortedHeaders = Object.keys(headers)
    .sort()
    .map((key) => `${key.toLowerCase()}:${headers[key].trim()}\n`)
    .join('');

  const signedHeaders = Object.keys(headers)
    .sort()
    .map((key) => key.toLowerCase())
    .join(';');

  const hashedPayload = CryptoJS.SHA256(payload).toString(CryptoJS.enc.Hex);

  return [
    method,
    path,
    canonicalQueryString,
    sortedHeaders,
    signedHeaders,
    hashedPayload,
  ].join('\n');
}

function buildStringToSign(
  dateTime: string,
  credentialScope: string,
  hashedCanonicalRequest: string,
): string {
  return [
    'AWS4-HMAC-SHA256',
    dateTime,
    credentialScope,
    hashedCanonicalRequest,
  ].join('\n');
}

function calculateSigningKey(
  secretKey: string,
  dateTime: string,
  region: string,
  service: string,
): CryptoJS.lib.WordArray {
  const dateStamp = dateTime.slice(0, 8);
  const kDate = CryptoJS.HmacSHA256(dateStamp, `AWS4${secretKey}`);
  const kRegion = CryptoJS.HmacSHA256(region, kDate);
  const kService = CryptoJS.HmacSHA256(service, kRegion);
  const kSigning = CryptoJS.HmacSHA256('aws4_request', kService);
  return kSigning;
}

// Creates and returns a MQTT connection with identity creds
function buildMQTTConnection(
  endpoint: string,
  region: string,
  credentials: Credentials,
  clientId: string,
): MqttClient {
  const wsUrl = createWebSocketUrl(endpoint, region, credentials);

  const options = {
    clientId,
    clean: true,
    protocol: 'wss' as const,
    keepalive: 30,
    reconnectPeriod: 1000,
    connectTimeout: 30 * 1000,
    will: undefined,
    rejectUnauthorized: true,
  };

  return mqtt.connect(wsUrl, options);
}

async function getDashboardUser(): Promise<DashboardUser | undefined> {
  try {
    const res = await axios.get(formatUrl(restHost, 'customer'), {
      headers: {
        Authorization: `Bearer ${getCookie('access_token')}`,
      },
    });
    return res.data;
  } catch (err: any) {
    console.error(err);
    if (err.response?.status === 401) {
      deleteToken();
    }
    toast.error(err.response?.data?.message);
  }
}

async function attachIoTPolicy(identity_id: string) {
  const params = {
    identity_id,
  };
  try {
    await axios.put(
      formatUrl(restHost, 'auth/identity/attach_iot_policy'),
      params,
      {
        headers: {
          Authorization: `Bearer ${getCookie('access_token')}`,
        },
      },
    );
  } catch (err: any) {
    console.error(err);
    if (err.response?.status === 401) {
      deleteToken();
    }
    toast.error(err.response?.data?.message);
  }
}

// uses id token to get a Cognito identity for MQTT authentication
async function getIdentity(region: string, token: string) {
  const client = new CognitoIdentityClient({ region });
  const loginsParam: any = {
    Logins: {
      [`cognito-idp.${region}.amazonaws.com/${REACT_APP_AWS_COGNITO_USER_POOL_ID}`]:
        token,
    },
  };
  const getIdCommand = new GetIdCommand({
    IdentityPoolId: REACT_APP_AWS_COGNITO_IDENTITY_POOL_ID,
    ...loginsParam,
  });
  const identity = await client.send(getIdCommand);
  const command = new GetCredentialsForIdentityCommand({
    IdentityId: identity.IdentityId,
    ...loginsParam,
  });
  return client.send(command);
}

const mqttMiddleware = () => {
  let connection: MqttClient | null = null;

  const handleConnectionStatusesUpdate = (
    store: MQTTStore,
    payload: MQTTConnectionEvent,
  ) => {
    const {
      customers: { facilityCustomers },
      devices: { selectedDevice },
    } = store.getState();
    const { clientId, eventType } = payload;

    if (facilityCustomers && clientId.startsWith('mobile-')) {
      store.dispatch(
        populateCustomers(
          (facilityCustomers || []).map((customer: Owner) => {
            const updatedCustomer =
              customer.customer_id ===
              Number(clientId.split('-').slice(-2, -1)[0]);
            if (updatedCustomer) {
              return {
                ...customer,
                is_online: eventType === 'connected',
              };
            }
            return customer;
          }),
        ),
      );
    }

    const connStatus = eventType === 'connected' ? 'online' : 'offline';
    store.dispatch(updateDeviceStatus(clientId, connStatus));
    store.dispatch(updateFacilityDeviceStatus(clientId, connStatus));
    if (selectedDevice && selectedDevice.serial_number === clientId) {
      store.dispatch(updateSelectedDeviceStatus(connStatus));
    }
    return;
  };

  const onMQTTMessageReceived = (store: MQTTStore) => {
    return (topic: string, payload: Buffer) => {
      let message = getJSONOrString(payload.toString('utf8'));
      const selfCustomerId =
        store.getState().dashboardUser?.dashboardUserInfo?.customer_id;

      // Regex pattern to match both "connected" and "disconnected" topics
      const connectionTopicPattern =
        /^\$aws\/events\/presence\/(connected|disconnected)\/[^/]+$/;
      if (connectionTopicPattern.test(topic)) {
        handleConnectionStatusesUpdate(store, message);
      } else if (
        // Self-bind: the local broker has no per-topic authz, so a bare suffix match would let a forged ring point our media at an attacker Janus.
        selfCustomerId != null &&
        topic === `omcare/users/${selfCustomerId}/calls/incoming`
      ) {
        // omcare/users/<id>/calls/incoming — Janus incoming-call ring (OI-1564).
        const incoming = parseIncomingCall(message);
        if (incoming) {
          store.dispatch(setIncomingCall(incoming));
        }
      } else if (/^omcare\/bus\/[^/]+\/calls\/status$/.test(topic)) {
        // omcare/bus/<chat_id>/calls/status — call lifecycle (OI-1564).
        const status = parseCallStatus(message);
        if (status) {
          store.dispatch(setCallStatus(status));
        }
      } else {
        console.log(
          `MQTT message received in topic: ${topic}\n${JSON.stringify(message)}`,
        );
      }
    };
  };

  // the middleware part of this function
  return (store: MQTTStore) =>
    (next: any) =>
    async (action: MQTTStoreAction) => {
      if (!action) {
        return console.log('mqtt action was null?');
      }

      // Per-call Janus bus-status subscribe/unsubscribe for the in-page v2 call
      // (OI-1564) — chat_id is only known once a call starts/is accepted.
      const callsType = action.type as unknown as CallsActionTypes;
      if (
        callsType === CallsActionTypes.SUBSCRIBE_CALL_STATUS ||
        callsType === CallsActionTypes.UNSUBSCRIBE_CALL_STATUS
      ) {
        const chatId = (action as unknown as { payload: { chatId: string } })
          .payload?.chatId;
        const topic = chatId ? `omcare/bus/${chatId}/calls/status` : null;
        if (topic && connection) {
          if (callsType === CallsActionTypes.SUBSCRIBE_CALL_STATUS) {
            connection.subscribe(topic, { qos: 0 }, (err) =>
              err
                ? console.error(`Failed to subscribe to ${topic}:`, err)
                : console.log(`Subscribed to ${topic}`),
            );
          } else {
            connection.unsubscribe(topic);
          }
        }
        return next(action);
      }

      switch (action.type) {
        case MQTTStoreActionTypes.MQTT_CONNECT: {
          const { host, token } = action.payload;
          if (connection !== null) {
            connection.end();
          }

          if (!host) {
            console.error('MQTT_CONNECT: host must be provided');
            break;
          }

          // Only connect once logged in — else the login page loops on getDashboardUser 401.
          if (!token) {
            console.error('MQTT_CONNECT: id token must be provided');
            break;
          }

          // wss:// host = local mosquitto (plain MQTT, no Cognito/SigV4); bare hostname = AWS IoT.
          const isLocalBroker = host.includes('://');

          // connect to the remote host
          console.log('opening mqtt connection');
          try {
            const dashboardUser = await getDashboardUser();
            const mqttClientId = `${REACT_APP_MQTT_CLIENT_PREFIX}${dashboardUser?.customer_id}`;
            const fullClientId = `${mqttClientId}-${Math.floor(Math.random() * 100000000)}`;

            if (isLocalBroker) {
              connection = mqtt.connect(host, {
                clientId: fullClientId,
                clean: true,
                protocol: 'wss',
                keepalive: 30,
                reconnectPeriod: 1000,
                connectTimeout: 30 * 1000,
              });
            } else {
              const awsRegion = host.split('.')[2];
              const identity = await getIdentity(awsRegion, token);
              await attachIoTPolicy(identity.IdentityId ?? '');
              connection = buildMQTTConnection(
                host,
                awsRegion,
                identity.Credentials ?? {},
                fullClientId,
              );
            }

            connection.on('connect', async () => {
              store.dispatch(mqttConnected());

              // Presence is AWS-IoT only — skip it on the local broker.
              const topicSubscriptions = isLocalBroker
                ? []
                : [
                    '$aws/events/presence/connected/#',
                    '$aws/events/presence/disconnected/#',
                  ];

              // Janus incoming-call ring for this user (OI-1564). Per-call bus
              // status (omcare/bus/<chat>/calls/status) is subscribed
              // dynamically by the call controller once chat_id is known.
              if (dashboardUser?.customer_id) {
                topicSubscriptions.push(
                  `omcare/users/${dashboardUser.customer_id}/calls/incoming`,
                );
              }

              topicSubscriptions.forEach((topic) => {
                connection?.subscribe(topic, { qos: 0 }, (err) => {
                  if (err) {
                    console.error(`Failed to subscribe to ${topic}:`, err);
                  } else {
                    console.log(`Subscribed to ${topic}`);
                  }
                });
              });

              if (publishMessageQueue.length) {
                console.log('processing through mqtt publish queue');
                publishMessageQueue.forEach((entry) => {
                  connection?.publish(entry.topic, entry.msg, { qos: 0 });
                });
                publishMessageQueue = [];
              }
            });

            connection.on('disconnect', () => {
              console.log('MQTT disconnected');
              store.dispatch(mqttDisconnected());
            });

            connection.on('message', onMQTTMessageReceived(store));

            connection.on('error', (err) => {
              console.error('MQTT connection error:', err);
            });

            connection.on('close', () => {
              console.log('MQTT connection closed');
              store.dispatch(mqttDisconnected());
            });
          } catch (err) {
            console.error('Error with mqtt connection: ', err);
            break;
          }

          break;
        }
        case MQTTStoreActionTypes.MQTT_DISCONNECT: {
          if (connection !== null) {
            connection.end();
          }
          connection = null;
          console.log('mqtt connection closed');
          break;
        }
        case MQTTStoreActionTypes.MQTT_PUBLISH: {
          const { topic, msg } = action.payload;
          console.log(
            `publishing to mqtt topic: "${topic}"\nmessage: ${JSON.stringify(
              msg,
            )}`,
          );
          if (!connection || !connection.connected) {
            publishMessageQueue.push({ topic, msg: JSON.stringify(msg) });
            break;
          }

          try {
            connection.publish(topic, JSON.stringify(msg), { qos: 0 });
          } catch (err) {
            console.error('Failed to publish message:', err);
          }
          break;
        }
        default:
          return next(action);
      }
      return next(action);
    };
};

export default mqttMiddleware();
